import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const CommunityServiceTracker = ({ user, db, isAdmin, isCommunityServiceAdmin }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [monthlyData, setMonthlyData] = useState({});
    const [monthlyTotal, setMonthlyTotal] = useState(0);
    const [editLockDate, setEditLockDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editModal, setEditModal] = useState({ show: false, day: null, data: null });

    // Get first day of month
    const getFirstOfMonth = (date = new Date()) => {
        const d = new Date(date);
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    // Format date as YYYY-MM-DD
    const formatDate = (date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Get all days in month with proper calendar layout
    const getMonthDays = (firstDay) => {
        const month = firstDay.getMonth();

        // Get the first day of the week for the calendar (Sunday)
        const firstCalendarDay = new Date(firstDay);
        firstCalendarDay.setDate(1 - firstDay.getDay()); // Go back to Sunday

        // Get 6 weeks worth of days (42 days) to fill the calendar
        const days = [];
        for (let i = 0; i < 42; i++) {
            const date = new Date(firstCalendarDay);
            date.setDate(firstCalendarDay.getDate() + i);
            days.push({
                date: date,
                isCurrentMonth: date.getMonth() === month,
                isToday: date.toDateString() === new Date().toDateString()
            });
        }

        return days;
    };

    useEffect(() => {
        const firstOfMonth = getFirstOfMonth();
        setCurrentMonth(firstOfMonth);
        loadEditLockDate();
    }, []);

    useEffect(() => {
        if (currentMonth && user) {
            loadMonthlyData();
        }
    }, [currentMonth, user]);

    const loadEditLockDate = async () => {
        try {
            const configDoc = await getDoc(doc(db, 'community-service-config', 'settings'));
            if (configDoc.exists()) {
                const data = configDoc.data();
                setEditLockDate(new Date(data.editLockDate || Date.now() - (14 * 24 * 60 * 60 * 1000)));
            } else {
                // Default to 2 weeks ago
                const twoWeeksAgo = new Date();
                twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                setEditLockDate(twoWeeksAgo);
            }
        } catch (error) {
            console.error('Error loading edit lock date:', error);
            const twoWeeksAgo = new Date();
            twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
            setEditLockDate(twoWeeksAgo);
        }
    };

    const loadMonthlyData = async () => {
        setLoading(true);
        try {
            // Load all weeks in the current month
            const monthDays = getMonthDays(currentMonth);
            const allData = {};

            // Group days by week (Monday-Sunday) to match our database structure
            const weeks = new Map();

            monthDays.forEach(({ date, isCurrentMonth }) => {
                if (!isCurrentMonth) return; // Only load current month data

                // Get Monday of this week
                const monday = new Date(date);
                const dayOfWeek = date.getDay();
                const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
                monday.setDate(diff);
                monday.setHours(0, 0, 0, 0);

                const weekKey = formatDate(monday);
                if (!weeks.has(weekKey)) {
                    weeks.set(weekKey, []);
                }
                weeks.get(weekKey).push(date);
            });

            // Load data for each week
            let monthTotalHours = 0;

            for (const [weekStart] of weeks) {
                try {
                    const docRef = doc(db, 'community-service-logs', `${user.email}_${weekStart}`);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const weekData = docSnap.data();
                        const dailyLogs = weekData.dailyLogs || {};

                        // Add this week's data to allData
                        Object.keys(dailyLogs).forEach(dateKey => {
                            allData[dateKey] = dailyLogs[dateKey];
                            monthTotalHours += dailyLogs[dateKey].hours || 0;
                        });
                    }
                } catch (error) {
                    console.error(`Error loading week ${weekStart}:`, error);
                }
            }

            setMonthlyData(allData);
            setMonthlyTotal(monthTotalHours);
        } catch (error) {
            console.error('Error loading monthly data:', error);
        }
        setLoading(false);
    };

    const saveWeeklyData = async (date, dayData) => {
        try {
            // Get Monday of the week for this date
            const monday = new Date(date);
            const dayOfWeek = date.getDay();
            const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            monday.setDate(diff);
            monday.setHours(0, 0, 0, 0);

            const weekId = formatDate(monday);
            const docRef = doc(db, 'community-service-logs', `${user.email}_${weekId}`);

            // Get existing week data
            const docSnap = await getDoc(docRef);
            let weekData = { dailyLogs: {} };
            if (docSnap.exists()) {
                weekData = docSnap.data();
            }

            // Update the specific day
            const dayKey = formatDate(date);
            weekData.dailyLogs[dayKey] = dayData;

            // Recalculate week total
            weekData.weekTotal = Object.values(weekData.dailyLogs).reduce((sum, day) => sum + (day.hours || 0), 0);
            weekData.lastUpdated = new Date().toISOString();
            weekData.userId = user.email;
            weekData.userName = user.displayName;
            weekData.weekStartDate = weekId;

            await setDoc(docRef, weekData, { merge: true });

            // Reload monthly data to refresh the view
            loadMonthlyData();
        } catch (error) {
            console.error('Error saving weekly data:', error);
            alert('Error saving data. Please try again.');
        }
    };

    const handleCellClick = (date, dayData) => {
        // Check if date is locked for editing
        if (editLockDate && date < editLockDate && !isAdmin) {
            alert(`Editing is locked for dates before ${editLockDate.toLocaleDateString()}`);
            return;
        }

        setEditModal({
            show: true,
            day: formatDate(date),
            data: dayData || { hours: 0, comment: '' }
        });
    };

    const handleSaveEdit = async (dayKey, hours, comment) => {
        // Validate hours
        const numHours = parseFloat(hours) || 0;
        if (numHours < 0 || numHours > 25) {
            alert('Hours must be between 0 and 25');
            return;
        }

        const dayData = {
            hours: numHours,
            comment: comment.trim(),
            lastUpdated: new Date().toISOString()
        };

        await saveWeeklyData(new Date(dayKey + 'T00:00:00'), dayData);
        setEditModal({ show: false, day: null, data: null });
    };

    const navigateMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
    };

    const monthDays = getMonthDays(currentMonth);
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const dayHeaders = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-gray-600">Loading community service data...</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-[#782953]">Community Service Log</h2>
                    <div className="text-sm text-gray-600">
                        Fall 2025 • {user.displayName}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Main Calendar View */}
                    <div className="lg:col-span-3">
                        {/* Month Navigation */}
                        <div className="flex justify-between items-center mb-4">
                            <button
                                onClick={() => navigateMonth(-1)}
                                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md text-gray-700 font-medium transition-colors"
                            >
                                ← Previous Month
                            </button>
                            <div className="font-bold text-xl text-[#782953]">
                                {monthName}
                            </div>
                            <button
                                onClick={() => navigateMonth(1)}
                                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md text-gray-700 font-medium transition-colors"
                            >
                                Next Month →
                            </button>
                        </div>

                        {/* Calendar Grid */}
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            {/* Day Headers */}
                            <div className="grid grid-cols-7 gap-0">
                                {dayHeaders.map(day => (
                                    <div key={day} className="p-3 text-center font-bold text-gray-600 bg-gray-50 border-b">
                                        {day.substring(0, 3)}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar Days */}
                            <div className="grid grid-cols-7 gap-0">
                                {monthDays.map(({ date, isCurrentMonth, isToday }, index) => {
                                    const dayKey = formatDate(date);
                                    const dayData = monthlyData[dayKey] || { hours: 0, comment: '' };
                                    const isLocked = editLockDate && date < editLockDate && !isAdmin;
                                    const isFuture = date > new Date();

                                    return (
                                        <div
                                            key={index}
                                            onClick={() => isCurrentMonth && !isLocked && handleCellClick(date, dayData)}
                                            className={`
                                                min-h-[120px] p-2 border-b border-r cursor-pointer transition-colors
                                                ${!isCurrentMonth ? 'bg-gray-50 opacity-70' : 'bg-white hover:bg-gray-50'}
                                                ${isCurrentMonth && !isLocked ? 'hover:bg-blue-50' : ''}
                                                ${isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}
                                                ${isFuture && isCurrentMonth ? 'bg-blue-50' : ''}
                                                ${dayData.hours > 0 && isCurrentMonth ? 'bg-green-50' : ''}
                                                ${isToday ? 'ring-2 ring-[#782953] ring-inset' : ''}
                                            `}
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div className={`font-bold text-sm ${
                                                    !isCurrentMonth ? 'text-gray-400' :
                                                    isToday ? 'text-[#782953]' : 'text-gray-900'
                                                }`}>
                                                    {date.getDate()}
                                                </div>
                                                {isToday && (
                                                    <div className="w-2 h-2 bg-[#782953] rounded-full"></div>
                                                )}
                                            </div>

                                            {isCurrentMonth && (
                                                <>
                                                    {dayData.hours > 0 && (
                                                        <div className="text-lg font-bold text-[#782953] mb-1">
                                                            {dayData.hours}h
                                                        </div>
                                                    )}
                                                    {dayData.comment && (
                                                        <div className="text-xs text-gray-500 overflow-hidden" style={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            lineHeight: '1.2'
                                                        }}>
                                                            {dayData.comment}
                                                        </div>
                                                    )}
                                                    {isLocked && (
                                                        <div className="text-xs text-red-500 mt-1 flex items-center">
                                                            <span className="mr-1">🔒</span>
                                                            <span>Locked</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Monthly Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="font-bold text-lg mb-4 text-[#782953]">Monthly Summary</h3>

                            <div className="mb-4">
                                <div className="text-3xl font-bold text-[#782953]">{monthlyTotal}h</div>
                                <div className="text-sm text-gray-600">Total This Month</div>
                            </div>

                            <div className="space-y-2 text-sm mb-4">
                                {/* Weekly breakdown */}
                                {(() => {
                                    const weeks = [];
                                    const monthStart = new Date(currentMonth);
                                    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

                                    // Get all Mondays in the month
                                    const firstMonday = new Date(monthStart);
                                    const dayOfWeek = firstMonday.getDay();
                                    firstMonday.setDate(firstMonday.getDate() - dayOfWeek + 1);
                                    if (firstMonday < monthStart) {
                                        firstMonday.setDate(firstMonday.getDate() + 7);
                                    }

                                    let currentMonday = new Date(firstMonday);
                                    while (currentMonday <= monthEnd) {
                                        const weekEnd = new Date(currentMonday);
                                        weekEnd.setDate(weekEnd.getDate() + 6);

                                        // Calculate hours for this week
                                        let weekHours = 0;
                                        for (let d = new Date(currentMonday); d <= weekEnd; d.setDate(d.getDate() + 1)) {
                                            if (d.getMonth() === currentMonth.getMonth()) {
                                                const dayKey = formatDate(d);
                                                weekHours += monthlyData[dayKey]?.hours || 0;
                                            }
                                        }

                                        weeks.push({
                                            start: new Date(currentMonday),
                                            hours: weekHours
                                        });

                                        currentMonday.setDate(currentMonday.getDate() + 7);
                                    }

                                    return weeks.map((week, index) => (
                                        <div key={index} className="flex justify-between">
                                            <span>Week {index + 1}</span>
                                            <span className="font-medium">{week.hours}h</span>
                                        </div>
                                    ));
                                })()}
                            </div>

                            <div className="pt-4 border-t border-gray-200">
                                <div className="text-xs text-gray-500">
                                    Click any day to log hours and add comments about your community service activities.
                                </div>
                            </div>

                            {editLockDate && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="text-xs text-gray-500">
                                        <span className="font-medium">Edit Lock:</span><br/>
                                        {editLockDate.toLocaleDateString()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {editModal.show && (
                <EditHoursModal
                    day={editModal.day}
                    data={editModal.data}
                    onSave={handleSaveEdit}
                    onClose={() => setEditModal({ show: false, day: null, data: null })}
                />
            )}
        </div>
    );
};

// Edit Hours Modal Component
const EditHoursModal = ({ day, data, onSave, onClose }) => {
    const [hours, setHours] = useState(data.hours || 0);
    const [comment, setComment] = useState(data.comment || '');

    const handleSave = () => {
        onSave(day, hours, comment);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    const dayOfWeek = new Date(day + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
    const displayDate = new Date(day + 'T00:00:00').toLocaleDateString();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4 text-[#782953]">
                    Log Community Service
                </h3>
                <div className="text-sm text-gray-600 mb-4">
                    {dayOfWeek}, {displayDate}
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hours *
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="25"
                            step="0.25"
                            value={hours}
                            onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
                            onKeyDown={handleKeyPress}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#782953]"
                            placeholder="0"
                            autoFocus
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            Maximum 25 hours per day
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            What did you do? (Optional)
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="w-full border border-gray-300 rounded px-3 py-2 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#782953]"
                            placeholder="Describe your community service activities..."
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-[#782953] text-white rounded hover:bg-opacity-90"
                    >
                        Save
                    </button>
                </div>

                <div className="text-xs text-gray-500 mt-3 text-center">
                    Tip: Press Ctrl+Enter to save quickly
                </div>
            </div>
        </div>
    );
};

export default CommunityServiceTracker;
